import {
  insightFieldKey,
  isInsightFieldMeasure,
  isInsightFieldRef,
  type BuiltinInsightDimension,
  type BuiltinInsightMeasure,
  type InsightBucket,
  type InsightDimension,
  type InsightFilter,
  type InsightMeasure,
  type InsightSegment,
  type InsightSpec,
  type InsightTransform,
} from './insight-spec'
import type { StreamHit } from './stream'
import { streamFieldLabels, streamFieldValue, type StreamNamedField } from './stream-columns'

// ── Computing a spec over rows ─────────────────────────────────────────────────────────────────

// What one row holds for a dimension or a measure, how rows are filtered and grouped into
// segments, and how a measure is carried along an axis. The pieces every insight computation
// (insight-compute.ts) and every reading of one (drill, critique, profile) shares.

/** The row fields an insight reads — structurally a `StreamHit`, so the pages pass theirs. */
export type InsightRow = Pick<
  StreamHit,
  | 'id'
  | 'mediaType'
  | 'outlet'
  | 'domain'
  | 'author'
  | 'folder'
  | 'topic'
  | 'topics'
  | 'tags'
  | 'publishedAt'
  | 'sentiment'
  | 'classification'
  | 'placement'
  | 'reach'
  | 'syndicationId'
  | 'isOrigin'
  | StreamNamedField
  | 'fields'
> &
  /** For the text filter; absent, a row matches no words. */
  Partial<Pick<StreamHit, 'headline' | 'snippet'>>

const MEDIA_LABELS: Record<StreamHit['mediaType'], string> = {
  news: 'News',
  broadcast: 'Broadcast',
  podcast: 'Podcast',
  social: 'Social',
  newsletter: 'Newsletter',
}

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1)

export const UNKNOWN = '(none)'

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const HOURS = Array.from({ length: 24 }, (_, hour) => `${String(hour).padStart(2, '0')}h`)
const REACH_BANDS = ['<1K', '1K–10K', '10K–100K', '100K–1M', '1M+']

/** Dimensions with a natural order: drawn in it, every bucket present, unless sorted by value. */
export const ORDINAL: Partial<Record<string, string[]>> = {
  weekday: WEEKDAYS,
  hour: HOURS,
  reachBand: REACH_BANDS,
}

/** Measures that add up across groups — the only ones an "Other" bucket, a percent stack or a
 *  running total means anything for. */
const ADDITIVE: ReadonlySet<string> = new Set(['count', 'reach'])
export const isAdditive = (measure: InsightMeasure) =>
  ADDITIVE.has(measure) || measure.startsWith('sum:')
/** Measures that are already a percentage (net sentiment is a percentage-point balance). */
export const PERCENT_MEASURES: ReadonlySet<string> = new Set([
  'netSentiment',
  'negativeShare',
  'positiveShare',
  'neutralShare',
  'pickupShare',
])

function reachBand(reach: number): string {
  if (reach < 1_000) return REACH_BANDS[0]!
  if (reach < 10_000) return REACH_BANDS[1]!
  if (reach < 100_000) return REACH_BANDS[2]!
  if (reach < 1_000_000) return REACH_BANDS[3]!
  return REACH_BANDS[4]!
}

/** Every value a row holds for a dimension — several for `topic` and `tag`, never zero (an unset
 *  label reads as "(none)", so it can still be counted and filtered). */
export function insightValues(
  row: InsightRow,
  field: InsightDimension,
  bucket: InsightBucket = 'day',
): string[] {
  if (isInsightFieldRef(field)) {
    // A build column: its labels (a yes/no reads Yes/No, a figure its number); none is "(none)".
    const labels = streamFieldLabels(streamFieldValue(row, insightFieldKey(field)))
    return labels.length > 0 ? [...new Set(labels)] : [UNKNOWN]
  }
  if (field === 'date') return [bucketKey(row.publishedAt, bucket)]
  return LABEL_VALUES[field](row)
}

/** Every built-in dimension but the date (which needs a bucket), as what one row holds for it. */
const LABEL_VALUES: Record<
  Exclude<BuiltinInsightDimension, 'date'>,
  (row: InsightRow) => string[]
> = {
  mediaType: (row) => [MEDIA_LABELS[row.mediaType] ?? row.mediaType],
  outlet: (row) => [row.outlet || UNKNOWN],
  domain: (row) => [row.domain || UNKNOWN],
  author: (row) => [row.author || UNKNOWN],
  folder: (row) => [row.folder || UNKNOWN],
  topic: (row) => {
    const topics = row.topics.length > 0 ? row.topics : [row.topic]
    const named = [...new Set(topics.filter(Boolean))]
    return named.length > 0 ? named : [UNKNOWN]
  },
  tag: (row) => (row.tags.length > 0 ? [...new Set(row.tags)] : [UNKNOWN]),
  sentiment: (row) => [row.sentiment ? capitalize(row.sentiment) : 'Unscored'],
  classification: (row) => [row.classification || 'Unclassified'],
  placement: (row) => [row.placement || UNKNOWN],
  syndication: (row) => [
    row.syndicationId === null ? 'Standalone' : row.isOrigin ? 'Origin' : 'Pickup',
  ],
  weekday: (row) => {
    const day = new Date(Date.parse(row.publishedAt)).getUTCDay()
    return [WEEKDAYS[(day + 6) % 7]!]
  },
  hour: (row) => [HOURS[new Date(Date.parse(row.publishedAt)).getUTCHours()] ?? UNKNOWN],
  reachBand: (row) => [reachBand(row.reach)],
  headline: (row) => {
    // Whitespace-normalised and capped at a filter value's length, so a headline read here
    // can be filtered on as it is.
    const headline = (row.headline ?? '').replace(/\s+/g, ' ').trim().slice(0, 200)
    return [headline || UNKNOWN]
  },
}

/** A day's Monday, by day — a year of rows has a few hundred distinct days, and building a Date
 *  per row (twice per draw) was the costliest thing a weekly chart did. */
const WEEK_OF = new Map<string, string>()

/** yyyy-mm-dd for a day, the Monday of a week, yyyy-mm for a month. */
export function bucketKey(iso: string, bucket: InsightBucket): string {
  const day = iso.slice(0, 10)
  if (bucket === 'month') return day.slice(0, 7)
  if (bucket === 'week') {
    let monday = WEEK_OF.get(day)
    if (monday === undefined) {
      const date = new Date(`${day}T00:00:00Z`)
      const offset = (date.getUTCDay() + 6) % 7
      monday = new Date(date.getTime() - offset * DAY_MS).toISOString().slice(0, 10)
      // Bounded: days are finite, but a decade of them is all this ever holds.
      if (WEEK_OF.size > 5_000) WEEK_OF.clear()
      WEEK_OF.set(day, monday)
    }
    return monday
  }
  return day
}

export const DAY_MS = 24 * 60 * 60 * 1000

/** Every bucket from the first key to the last, so a quiet day is a zero, not a missing point. */
export function bucketRange(first: string, last: string, bucket: InsightBucket) {
  const keys: string[] = []
  if (bucket === 'month') {
    let [year, month] = first.split('-').map(Number) as [number, number]
    const [lastYear, lastMonth] = last.split('-').map(Number) as [number, number]
    while (year < lastYear || (year === lastYear && month <= lastMonth)) {
      keys.push(`${year}-${String(month).padStart(2, '0')}`)
      month++
      if (month > 12) {
        month = 1
        year++
      }
      if (keys.length > 400) break
    }
    return keys
  }
  const step = bucket === 'week' ? 7 * DAY_MS : DAY_MS
  for (
    let at = Date.parse(`${first}T00:00:00Z`);
    at <= Date.parse(`${last}T00:00:00Z`) && keys.length <= 400;
    at += step
  ) {
    keys.push(new Date(at).toISOString().slice(0, 10))
  }
  return keys
}

export const round1 = (value: number) => Math.round(value * 10) / 10

export function insightMeasure(rows: InsightRow[], measure: InsightMeasure): number | null {
  if (isInsightFieldMeasure(measure)) return fieldMeasure(rows, measure)
  return MEASURES[measure](rows)
}

/** A build figure added up (`sum:`) or averaged (`avg:`) over the rows that carry one. */
function fieldMeasure(rows: InsightRow[], measure: `sum:${string}` | `avg:${string}`) {
  const key = measure.slice(4)
  let total = 0
  let carried = 0
  for (const row of rows) {
    const value = streamFieldValue(row, key)
    if (typeof value !== 'number') continue
    total += value
    carried += 1
  }
  if (carried === 0) return null
  const figure = measure.startsWith('sum:') ? total : total / carried
  return Math.round(figure * 100) / 100
}

/** A sentiment share, over the scored rows only — null when none is scored. `of` counts the
 *  sentiments the share is of (a difference, for the net balance). */
function sentimentShare(rows: InsightRow[], of: (count: (s: string) => number) => number) {
  const scored = rows.filter((row) => row.sentiment !== null)
  if (scored.length === 0) return null
  const count = (s: string) => scored.filter((row) => row.sentiment === s).length
  return round1((of(count) / scored.length) * 100)
}

function medianReach(rows: InsightRow[]) {
  if (rows.length === 0) return null
  const sorted = rows.map((row) => row.reach).sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid]! : Math.round((sorted[mid - 1]! + sorted[mid]!) / 2)
}

const MEASURES: Record<BuiltinInsightMeasure, (rows: InsightRow[]) => number | null> = {
  count: (rows) => rows.length,
  reach: (rows) => rows.reduce((sum, row) => sum + row.reach, 0),
  avgReach: (rows) =>
    rows.length === 0
      ? null
      : Math.round(rows.reduce((sum, row) => sum + row.reach, 0) / rows.length),
  medianReach,
  outlets: (rows) => new Set(rows.map((row) => row.outlet)).size,
  authors: (rows) => new Set(rows.map((row) => row.author).filter(Boolean)).size,
  pickupShare: (rows) =>
    rows.length === 0
      ? null
      : round1(
          (rows.filter((row) => row.syndicationId !== null && !row.isOrigin).length / rows.length) *
            100,
        ),
  netSentiment: (rows) => sentimentShare(rows, (count) => count('positive') - count('negative')),
  negativeShare: (rows) => sentimentShare(rows, (count) => count('negative')),
  positiveShare: (rows) => sentimentShare(rows, (count) => count('positive')),
  neutralShare: (rows) => sentimentShare(rows, (count) => count('neutral') + count('mixed')),
}

/** Each filter's values, lowercased into a set once — a chart over 25,000 rows with six segments
 *  otherwise rebuilds the same set for every row (measured: 140 ms a draw). Keyed by the filter
 *  object, so a spec's filters are compiled once per spec. */
const WANTED = new WeakMap<object, Set<string>>()

function wantedOf(filter: { values: string[] }): Set<string> {
  let wanted = WANTED.get(filter)
  if (!wanted) {
    wanted = new Set(filter.values.map((value) => value.toLowerCase()))
    WANTED.set(filter, wanted)
  }
  return wanted
}

/** A row's values for a field, lowercased — cached per row object. Rows come from the query cache
 *  and outlive any one draw, so every later chart over them filters without re-deriving them. */
const LOWERED = new WeakMap<InsightRow, Partial<Record<string, string[]>>>()

function loweredValues(row: InsightRow, field: Exclude<InsightDimension, 'date'>): string[] {
  let fields = LOWERED.get(row)
  if (!fields) {
    fields = {}
    LOWERED.set(row, fields)
  }
  let values = fields[field]
  if (values === undefined) {
    values = insightValues(row, field).map((value) => value.toLowerCase())
    fields[field] = values
  }
  return values
}

/** A row's headline and snippet, lowercased once — what the text filter searches. */
const WORDS = new WeakMap<InsightRow, string>()

function wordsOf(row: InsightRow): string {
  let words = WORDS.get(row)
  if (words === undefined) {
    words = `${row.headline ?? ''} ${row.snippet ?? ''}`.toLowerCase()
    WORDS.set(row, words)
  }
  return words
}

const compare = (value: number, op: 'gt' | 'gte' | 'lt' | 'lte', bound: number) =>
  op === 'gt'
    ? value > bound
    : op === 'gte'
      ? value >= bound
      : op === 'lt'
        ? value < bound
        : value <= bound

export function matchesInsightFilters(row: InsightRow, where: InsightFilter[]): boolean {
  return where.every((filter) => {
    if (filter.field === 'reach') return compare(row.reach, filter.op, filter.value)
    if (filter.field === 'text') {
      const words = wordsOf(row)
      const hit = [...wantedOf(filter)].some((needle) => words.includes(needle))
      return filter.op === 'contains' ? hit : !hit
    }
    const wanted = wantedOf(filter)
    const hit = loweredValues(row, filter.field).some((value) => wanted.has(value))
    return filter.op === 'in' ? hit : !hit
  })
}

/** A spec's segments with unique names — two segments the model named alike would otherwise
 *  merge into one series. The second becomes "Name (2)". Cached per spec so the filters inside
 *  stay the same objects (and stay compiled). */
const SEGMENTS = new WeakMap<InsightSpec, InsightSegment[]>()

export function insightSegments(spec: InsightSpec): InsightSegment[] {
  let segments = SEGMENTS.get(spec)
  if (!segments) {
    const seen = new Map<string, number>()
    segments = spec.segments.map((segment) => {
      const count = (seen.get(segment.name) ?? 0) + 1
      seen.set(segment.name, count)
      return count === 1 ? segment : { ...segment, name: `${segment.name} (${count})` }
    })
    SEGMENTS.set(spec, segments)
  }
  return segments
}

/** The names of the segments a row falls in — several, one or none. */
export const segmentNamesOf = (segments: InsightSegment[], row: InsightRow) =>
  segments.filter((segment) => matchesInsightFilters(row, segment.where)).map((s) => s.name)

/** A measure carried along the axis — running total, rolling mean, share of the column, or the
 *  change on the previous bucket. */
export function applyInsightTransform(
  values: (number | null)[],
  transform: InsightTransform,
  window = 7,
): (number | null)[] {
  switch (transform) {
    case 'none':
      return values
    case 'cumulative': {
      let sum = 0
      return values.map((value) => (sum += value ?? 0))
    }
    case 'rolling':
      return values.map((_, index) => {
        const span = values
          .slice(Math.max(0, index - window + 1), index + 1)
          .filter((v): v is number => v !== null)
        return span.length === 0 ? null : round1(span.reduce((a, b) => a + b, 0) / span.length)
      })
    case 'shareOfTotal': {
      const total = values.reduce<number>((sum, value) => sum + (value ?? 0), 0)
      return values.map((value) =>
        value === null ? null : total === 0 ? 0 : round1((value / total) * 100),
      )
    }
    case 'change':
      return values.map((value, index) => {
        const previous = index > 0 ? values[index - 1] : null
        if (value === null || previous === null || previous === undefined || previous === 0) {
          return null
        }
        return round1(((value - previous) / Math.abs(previous)) * 100)
      })
  }
}

export function transformLabel(label: string, transform: InsightTransform, window: number): string {
  switch (transform) {
    case 'none':
      return label
    case 'cumulative':
      return `${label}, running total`
    case 'rolling':
      return `${label}, ${window}-point average`
    case 'shareOfTotal':
      return `${label}, % of total`
    case 'change':
      return `${label}, % change`
  }
}

export const transformIsPercent = (transform: InsightTransform) =>
  transform === 'shareOfTotal' || transform === 'change'
